/* The Android/iOS shells cannot sell Pro, but they can help a user get to the
 * place that does. Two ways, both of which leave the app as TEXT and never as a
 * tap that opens a page:
 *
 *   email  the link is mailed to the account's own verified address (the same
 *          mail/ queue and Resend relay as the weekly recap), and
 *   copy   the link goes to the clipboard.
 *
 * Neither is an <a>, a window.open or a Capacitor Browser call — the shell
 * still contains no element that navigates to a purchase surface, and
 * e2e/native-store-policy.spec.js pins exactly that. The user is the one who
 * opens the link, in their own browser. TODO.md › Rules that bind carries the
 * policy reasoning and the risk that goes with it.
 *
 * Every link is tagged (utm_*), so the website sees WHERE a visitor came from
 * and Analytics/attribution.js can tell an email click from a pasted link:
 *
 *   utm_source    android_app | ios_app   the shell the user was in
 *   utm_medium    handoff_email | handoff_copy
 *   utm_campaign  pro_annual
 *   utm_content   the paywall source that led here (nav, locked line, …)
 *
 * `pro=open` makes the web app open the paywall on arrival (App.jsx) so the
 * click lands on the plan buttons instead of a dashboard. */
import { doc, setDoc } from "firebase/firestore";
import { db } from "./firebase.js";
import { emailDeliveryAvailable } from "./appConfig.js";
import { platform } from "./native.js";
import { t } from "./i18n.js";

export const PRO_SITE = "https://towardsgm.com";
export const HANDOFF_FLAG = "pro";
export const HANDOFF_OPEN = "open";

const BRAND = "#8A6A2F";
const INK = "#241F1A";
const SUB = "#6B6259";

export function handoffUrl({ medium, source, from = platform } = {}) {
  const params = new URLSearchParams({
    [HANDOFF_FLAG]: HANDOFF_OPEN,
    utm_source: `${from}_app`,
    utm_medium: `handoff_${medium}`,
    utm_campaign: "pro_annual",
  });
  if (source) params.set("utm_content", source);
  return `${PRO_SITE}/?${params}`;
}

/* Why the email button is not offered, or null if it can be. Checked up front
 * so a user is never shown a button that is certain to fail — copy is then the
 * only action, and that is a complete experience on its own. */
export async function emailBlockedReason(user) {
  if (!db || !user?.uid || user.isAnonymous || !user.email) return "no_account";
  if (user.emailVerified === false) return "unverified";
  if (!(await emailDeliveryAvailable())) return "unavailable";
  return null;
}

const escapeHtml = (s) =>
  String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);

/* Pure and dull on purpose, like renderRecapEmail: inline styles, no images. */
export function renderHandoffEmail({ url, email }) {
  const steps = [t("proHandoff.email.step1"), t("proHandoff.email.step2", { email }), t("proHandoff.email.step3"), t("proHandoff.email.step4")];
  const html = `<div style="background:#FBF8F3;padding:24px 0;font-family:Georgia,'Times New Roman',serif;color:${INK}">
  <div style="max-width:520px;margin:0 auto;background:#fff;border:1px solid #E5DED2;border-radius:14px;padding:24px">
    <div style="font-family:'Courier New',monospace;font-size:11px;letter-spacing:1px;color:${SUB}">TOWARDSGM PRO</div>
    <div style="font-size:22px;line-height:1.3;margin:8px 0 14px">${escapeHtml(t("proHandoff.email.headline"))}</div>
    <ol style="margin:0 0 22px;padding-left:20px;font-size:15px;line-height:1.7">${steps.map((s) => `<li>${escapeHtml(s)}</li>`).join("")}</ol>
    <a href="${escapeHtml(url)}" style="display:inline-block;background:${BRAND};color:#fff;text-decoration:none;border-radius:999px;padding:11px 20px;font-family:'Courier New',monospace;font-size:13px">${escapeHtml(t("proHandoff.email.cta"))}</a>
    <div style="margin-top:18px;font-family:Arial,sans-serif;font-size:12px;line-height:1.6;color:${SUB}">${escapeHtml(t("proHandoff.email.fallback"))}<br><span style="word-break:break-all">${escapeHtml(url)}</span></div>
  </div>
  <div style="max-width:520px;margin:14px auto 0;font-family:Arial,sans-serif;font-size:11px;line-height:1.6;color:${SUB};text-align:center">${escapeHtml(t("proHandoff.email.why"))}</div>
</div>`;
  return {
    subject: t("proHandoff.email.subject"),
    text: [t("proHandoff.email.headline"), "", ...steps.map((s, i) => `${i + 1}. ${s}`), "", url, "", t("proHandoff.email.why")].join("\n"),
    html,
  };
}

const todayKey = (now = new Date()) => now.toISOString().slice(0, 10);

/* Queue the link for the signed-in user's own address.
 *
 * Resolves { ok: true } | { ok: false, reason } — the reason is analytics-safe
 * (no addresses). firestore.rules is what stops this being a spam cannon, as
 * with the recap: `to` must be the sender's own verified address, and the id
 * `{uid}_pro-{date}` under create-only makes it one message per user per day.
 * A second tap the same day is refused by the rules and reported as
 * "already_sent" — true, and the user is told to check their inbox. */
export async function sendHandoffEmail(user, { source, now = new Date() } = {}) {
  const blocked = await emailBlockedReason(user);
  if (blocked) return { ok: false, reason: blocked };

  const weekKey = `pro-${todayKey(now)}`;
  const ref = doc(db, "mail", `${user.uid}_${weekKey}`);
  try {
    /* No existence check first. mail/ is readable only by `resource.data.uid ==
     * auth.uid`, and for a document that does not exist `resource` is null, so
     * that read is DENIED — a pre-read here fails exactly when there is
     * nothing to find. The create-only rule is the check instead: a second
     * create the same day is an update, which the rules refuse. */
    await setDoc(ref, {
      uid: user.uid,
      weekKey,
      kind: "pro_handoff",
      to: user.email,
      queuedAt: now.getTime(),
      message: renderHandoffEmail({ url: handoffUrl({ medium: "email", source }), email: user.email }),
    });
    return { ok: true, reason: "queued" };
  } catch (err) {
    /* Everything the rules also check was verified above (own verified address,
     * the id shape), so a refusal at this point is the one-per-day bound: the
     * link is already in the inbox. */
    if (err?.code === "permission-denied") return { ok: true, reason: "already_sent" };
    return { ok: false, reason: err?.code || "write_failed" };
  }
}

/* Clipboard, with the old execCommand path as the fallback: a WebView without
 * a secure-context clipboard (or one that refuses without a gesture) should
 * still copy from a tap. Resolves whether the text is believed to be on the
 * clipboard — never throws. */
export async function copyHandoffLink({ source } = {}) {
  const url = handoffUrl({ medium: "copy", source });
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      return true;
    }
  } catch {
    /* fall through to the textarea path */
  }
  try {
    const el = document.createElement("textarea");
    el.value = url;
    el.setAttribute("readonly", "");
    el.style.cssText = "position:fixed;opacity:0;top:0;left:0";
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand("copy");
    el.remove();
    return ok;
  } catch {
    return false;
  }
}
